import { createWithEqualityFn } from 'zustand/traditional';

import { MajorsResult, majorsDescriptionMap } from '@common-types';

import { trackEvent } from '@utils/googleAnalytics';

import useHomePageStore from '@store/HomePageStore';

import { baseService } from '@services/base.service';

import { addMajorFavorite, deleteFavoriteMajor } from '@api/userPreferences';

import useNotificationStore from '@store/NotificationStore';

import useUserPreferencesStore from '@store/UserPreferencesStore';

export interface FavoriteMajorsStore {
  favoriteMajors: MajorsResult;
  isFavoriteMajorsLoading: boolean;
  favoriteMajorsCipCodes: string[];
  unfavoritingMajors: Set<string>;
  getMajorsByCipCodes: (cipCodes: string[]) => void;
  toggleFavoriteMajors: (majorCipCode: string) => Promise<void>;
  addFavoriteMajor: (cipCode: string) => Promise<void>;
  deleteFavoriteMajor: (cipCode: string) => Promise<void>;
}

const initialState = {
  favoriteMajorsCipCodes: [] as string[],
  isFavoriteMajorsCipCodesLoading: false,
  favoriteMajors: {} as MajorsResult,
  isFavoriteMajorsLoading: false,
  unfavoritingMajors: new Set<string>(),
};

// Subscribe to UserPreferencesStore changes
useUserPreferencesStore.subscribe((state) => {
  const cipCodes = state.majorFavorites.cipCodes;
  if (cipCodes) {
    if (
      !useFavoriteMajorsStore.getState().favoriteMajorsCipCodes.every((cipCode: string) => cipCodes.includes(cipCode))
    ) {
      useFavoriteMajorsStore.setState({ favoriteMajorsCipCodes: cipCodes });
      useFavoriteMajorsStore.getState().getMajorsByCipCodes(cipCodes);
    }
  }
});

export const useFavoriteMajorsStore = createWithEqualityFn<FavoriteMajorsStore>()(
  (set, get) => ({
    ...initialState,

    getMajorsByCipCodes: async (cipCodes: string[]) => {
      if (cipCodes.length) {
        set({ isFavoriteMajorsLoading: true });

        const favoriteMajors = await baseService.getAsync<MajorsResult>(
          `/majors?exactCipCodes=${cipCodes?.join(',')}&limit=${cipCodes.length}`,
        );

        set({ favoriteMajors, isFavoriteMajorsLoading: false });
      } else {
        set({ favoriteMajors: {} as MajorsResult });
      }

      return get().favoriteMajors;
    },

    toggleFavoriteMajors: async (majorCipCode: string) => {
      const { addFavoriteMajor, deleteFavoriteMajor } = get();

      const selectedMajor = useUserPreferencesStore
        .getState()
        .majorFavorites.cipCodes?.find((cipCode: string) => cipCode === majorCipCode);

      if (!selectedMajor) {
        await addFavoriteMajor(majorCipCode);

        useNotificationStore.getState().notify({
          message: `Saved to Favorites`,
          severity: 'info',
        });
      } else {
        set((state) => ({
          unfavoritingMajors: new Set([...state.unfavoritingMajors, majorCipCode]),
          favoriteMajors: {
            ...state.favoriteMajors,
            majors: (state.favoriteMajors.majors ?? []).filter((major) => major.cipCode !== majorCipCode),
          },
        }));

        setTimeout(async () => {
          try {
            await deleteFavoriteMajor(majorCipCode);
          } catch (error) {
            console.error('Failed to delete favorite major:', error);
          }
          set((state) => ({
            unfavoritingMajors: new Set([...state.unfavoritingMajors].filter((cip) => cip !== majorCipCode)),
          }));

          useNotificationStore.getState().notify({
            message: `Removed from Favorites`,
            severity: 'info',
          });
        }, 1);
      }

      await useHomePageStore.getState().getLandingPageData();

      trackEvent({
        eventType: selectedMajor ? 'Unfavorite Major' : 'Favorite Major',
        props: {
          majorName: majorsDescriptionMap.get(majorCipCode)?.cipTitle,
          majorId: majorCipCode,
        },
      });
    },

    addFavoriteMajor: async (cipCode: string) => {
      await addMajorFavorite(cipCode);
    },

    deleteFavoriteMajor: async (cipCode: string) => {
      return await deleteFavoriteMajor(cipCode);
    },
  }),
  Object.is,
);
